/**
 * =============================================================================
 * Registro de CASAS del mundo (edificios multi-parte)
 * =============================================================================
 *
 * Cada casa es un gráfico de 256px de ancho (= 8 tiles) partido en BANDAS que se
 * SUPERPONEN con pequeños offsets "hacia atrás" (norte) para dar perspectiva, más un
 * TECHO. Los assets viven en `client/assets/world/houses/<key>.png` (fondo transparente)
 * y se indexan como decoraciones (`type: 'house'`, `subtype: '<houseId>'`) en
 * `client/assets/assets-index.json` para que aparezcan agrupadas por casa en la paleta.
 *
 * Modelo de armado (NO es apilado por altura, sino superposición con offset):
 *   - Cada parte se ancla `origin (0.5, 1)` al borde inferior-centro de un tile, en la
 *     columna `anchorX` y en la fila `baseY - tilesBack`. `tilesBack` = cuántos tiles
 *     hacia ATRÁS (norte) respecto de la base (la parte de adelante va con `tilesBack: 0`).
 *   - Las bandas más atrás (mayor `tilesBack`) tienen menor Y → menor depth en
 *     `ground_deco_over` → quedan DETRÁS; la de adelante (tilesBack 0) tapa a las de atrás
 *     y el personaje se ve delante/detrás según su Y (efecto AO).
 *   - El TECHO va a la capa `roof` (depth fijo, SIEMPRE encima del personaje), anclado en
 *     el mismo `tilesBack` que la banda trasera, así cubre la parte de arriba del edificio.
 *   - El PISO interior (`floor`) se estampa en `layer1_ground` SOLO adentro de las paredes (madera,
 *     `houseFloorSprite`, sub-tiles 1×1 sin bleed), así el interior no muestra el pasto de fondo y
 *     el piso no desborda la casa. Fluye por `sectors.ts` (`SectorOutput.groundTiles`) →
 *     `worldGenerator.ts`, así SOBREVIVE a regenerar el mundo (a diferencia de pintar tiles a mano).
 *
 * Las casas 1/2/3 comparten el mismo tamaño de bandas (_1=1, _2=5, _3=3 tiles de alto),
 * por eso usan los mismos offsets. La casa 4 es más chica (solo _1 y _2 de cuerpo).
 *
 * Para AGREGAR una casa: dropear sus PNG en `world/houses/`, indexarlas en
 * `assets-index.json`, y sumar un `HouseDef`. La colocación en ciudades
 * (`shared/world/sectors.ts`) y la paleta la toman automáticamente.
 */

import type { TileData, MapRect } from '../types/map';
import houseCollisionJson from './houseCollision.json';

/**
 * Offset de un tile bloqueado, RELATIVO al ancla de la casa:
 *   - `dx` = columna respecto de `anchorX` (centro de la base).
 *   - `dy` = fila respecto de `baseY` (base de la banda de adelante); normalmente `dy <= 0`
 *     (hacia el norte). Tile absoluto = `(anchorX + dx, baseY + dy)`.
 */
export interface HouseBlockOffset {
  dx: number;
  dy: number;
}

/**
 * Sidecar de colisión por casa (`houseId → offsets bloqueados`), editable con la herramienta
 * "Bloqueos de casas" del login (ver `client/src/scenes/houseEditor/HouseCollisionView.ts`) y
 * persistido por `server/src/routes/devHouses.ts`. Una casa con entrada acá usa esta lista como
 * colisión; una sin entrada cae al rectángulo paramétrico (`HouseDef.collision`).
 */
const HOUSE_COLLISION: Record<string, HouseBlockOffset[]> = houseCollisionJson as Record<
  string,
  HouseBlockOffset[]
>;

/**
 * Offsets de bloqueo explícitos de una casa, o `undefined` si nunca se editó (⇒ usa el rect).
 * Filtra entradas vacías para que `[]` guardado también caiga al fallback del rectángulo.
 */
export function houseBlockedOffsets(id: string): HouseBlockOffset[] | undefined {
  const list = HOUSE_COLLISION[id];
  return list && list.length > 0 ? list : undefined;
}

/**
 * Ancho DEFAULT de la huella, en tiles. Un sprite de 256px (8 tiles) anclado `origin 0.5`
 * al CENTRO de un tile pisa 9 columnas (`anchorX-4 .. anchorX+4`), por eso 9. Las casas más
 * anchas (ej. 320px = 10 tiles ⇒ 11 de huella) lo overridean con `HouseDef.widthTiles`.
 */
export const HOUSE_WIDTH_TILES = 9;

export type HouseLayer = 'ground_deco_over' | 'roof';

/** Una banda de la casa (cuerpo o techo). */
export interface HousePart {
  /** Key de decoración (= nombre de archivo sin `.png`). */
  key: string;
  /** Tiles hacia ATRÁS (norte) desde la base. La parte de adelante = 0. */
  tilesBack: number;
  layer: HouseLayer;
  /**
   * Alto del sprite en tiles (redondeado hacia arriba). Anclado `origin (0.5,1)` en la fila
   * `baseY - tilesBack`, la banda ocupa hacia el norte hasta `baseY - tilesBack - heightTiles
   * + 1`. Da el alto VISUAL real (techo incluido), MUCHO mayor que la huella de colisión, y es
   * lo que se usa para NO encimar casas en las ciudades (ver `houseVisualDepthTiles`).
   */
  heightTiles: number;
}

export interface HouseDef {
  id: string;
  name: string;
  /** Partes de la casa (cuerpo + techo). El orden no afecta el render (cada una tiene su offset). */
  parts: HousePart[];
  /** Ancho de la huella en tiles. Default `HOUSE_WIDTH_TILES` (9, para sprites de 256px). */
  widthTiles?: number;
  /**
   * Columnas de la PUERTA (vano caminable en la pared de adelante): offsets X desde `anchorX`.
   * Default `[1, 2]` = columnas 6 y 7 de las 9 de la huella (`anchorX` es la columna del centro).
   */
  doorCols?: number[];
  /**
   * Rectángulo de colisión (paredes), AJUSTADO al cuerpo visible —la casa está en
   * perspectiva, así que el rectángulo no arranca en la base del sprite—:
   * - `halfWidth`: tiles a cada lado de `anchorX` (default `floor(width/2)`).
   * - `front`/`back`: `tilesBack` (filas) de la pared de ADELANTE y de ATRÁS
   *   (`front < back`). Default `front=0`, `back=depth-1`.
   * Se bloquea el perímetro de ese rectángulo (menos la puerta) y el interior queda caminable.
   */
  collision?: { halfWidth?: number; front: number; back: number };
}

/** Ancho de la huella de una casa, en tiles. */
export function houseWidthTiles(def: HouseDef): number {
  return def.widthTiles ?? HOUSE_WIDTH_TILES;
}

/**
 * Piso interior de las casas: la celda de madera "Floor 2" (floors1, col1/row0 =
 * gridX 4..7 × gridY 0..3). Se rellena con **sub-tiles 1×1** en vez del surface
 * 4×4, porque el 4×4 se renderiza por tile con `origin 0.5` y SANGRA 3 tiles hacia
 * la derecha/abajo → el piso se escapaba de la casa. Con 1×1 (`tilesX/Y:1`) cada
 * tile pinta exactamente su celda; eligiendo el sub-tile por `(tx,ty) mod 4` se
 * reconstruye la textura de madera sin costuras y sin desbordar. Ver los surfaces
 * `floors1_<4..7>_<0..3>_1x1` en `client/assets/assets-index.json`.
 */
export function houseFloorSprite(tx: number, ty: number): string {
  const gx = 4 + (((tx % 4) + 4) % 4);
  const gy = ((ty % 4) + 4) % 4;
  return `floors1_${gx}_${gy}_1x1`;
}

/** Puerta por defecto: columnas 6 y 7 (offsets +1, +2 desde el centro `anchorX`). */
const DEFAULT_DOOR_COLS = [1, 2];

/**
 * Catálogo de casas. Offsets confirmados por Dalmiro para las casas 1/2/3
 * (_1 adelante, _2 a 1 tile, _3 a 5 tiles, techo a 3). La casa 4 es más chica.
 * Las 4 casas tienen techo (`houseN_roof`).
 */
export const HOUSES: Record<string, HouseDef> = {
  house1: {
    id: 'house1',
    name: 'Casa 1 (cabaña)',
    parts: [
      { key: 'house1_1', tilesBack: 0, layer: 'ground_deco_over', heightTiles: 1 },
      { key: 'house1_2', tilesBack: 1, layer: 'ground_deco_over', heightTiles: 5 },
      { key: 'house1_3', tilesBack: 5, layer: 'ground_deco_over', heightTiles: 3 },
      { key: 'house1_roof', tilesBack: 2, layer: 'roof', heightTiles: 10 },
    ],
    collision: { front: 2, back: 6, halfWidth: 4 },
  },
  house2: {
    id: 'house2',
    name: 'Casa 2 (piedra)',
    parts: [
      { key: 'house2_1', tilesBack: 0, layer: 'ground_deco_over', heightTiles: 1 },
      { key: 'house2_2', tilesBack: 1, layer: 'ground_deco_over', heightTiles: 5 },
      { key: 'house2_3', tilesBack: 5, layer: 'ground_deco_over', heightTiles: 3 },
      { key: 'house2_roof', tilesBack: 3, layer: 'roof', heightTiles: 6 },
    ],
    collision: { front: 2, back: 6, halfWidth: 4 },
  },
  house3: {
    id: 'house3',
    name: 'Casa 3 (ladrillo)',
    parts: [
      { key: 'house3_1', tilesBack: 0, layer: 'ground_deco_over', heightTiles: 1 },
      { key: 'house3_2', tilesBack: 1, layer: 'ground_deco_over', heightTiles: 5 },
      { key: 'house3_3', tilesBack: 5, layer: 'ground_deco_over', heightTiles: 3 },
      { key: 'house3_roof', tilesBack: 3, layer: 'roof', heightTiles: 6 },
    ],
    collision: { front: 2, back: 6, halfWidth: 4 },
  },
  house4: {
    id: 'house4',
    name: 'Casa 4 (chica)',
    parts: [
      { key: 'house4_1', tilesBack: 0, layer: 'ground_deco_over', heightTiles: 5 },
      { key: 'house4_2', tilesBack: 4, layer: 'ground_deco_over', heightTiles: 3 },
      { key: 'house4_roof', tilesBack: 2, layer: 'roof', heightTiles: 6 },
    ],
    collision: { front: 1, back: 6, halfWidth: 3 },
  },
  // Casa 6: 320px de ancho (10 tiles ⇒ huella 11). Casa con INTERIOR visible:
  // _1 = pared frontal (puerta + ventana), _2 = pared interior (cuadro/estantes), roof arriba.
  // Offsets ESTIMADOS — a confirmar visualmente con Dalmiro.
  house6: {
    id: 'house6',
    name: 'Casa 6 (ladrillo, interior)',
    widthTiles: 11,
    parts: [
      { key: 'house6_1', tilesBack: 0, layer: 'ground_deco_over', heightTiles: 7 },
      { key: 'house6_2', tilesBack: 6, layer: 'ground_deco_over', heightTiles: 3 },
      { key: 'house6_roof', tilesBack: 2, layer: 'roof', heightTiles: 9 },
    ],
    collision: { front: 1, back: 8, halfWidth: 5 },
  },
  // Casa 7: pared frontal (_1) + pared trasera (_2, al fondo) + techo. Offsets estimados
  // (afinar visualmente). La colisión fina se pinta con la herramienta "Bloqueos de casas".
  house7: {
    id: 'house7',
    name: 'Casa 7',
    parts: [
      { key: 'house7_1', tilesBack: 0, layer: 'ground_deco_over', heightTiles: 6 },
      { key: 'house7_2', tilesBack: 4, layer: 'ground_deco_over', heightTiles: 7 },
      { key: 'house7_roof', tilesBack: 3, layer: 'roof', heightTiles: 10 },
    ],
    collision: { front: 0, back: 4, halfWidth: 3 },
  },
};

/** Ids de casas disponibles (orden estable para selección determinista). */
export const HOUSE_IDS: string[] = Object.keys(HOUSES);

/**
 * Profundidad de la huella (tiles, front→back) = mayor `tilesBack` de las bandas de
 * cuerpo + 1. Es lo que bloquea/suprime vegetación en el suelo.
 */
export function houseFootprintDepthTiles(def: HouseDef): number {
  const maxBack = def.parts
    .filter((p) => p.layer !== 'roof')
    .reduce((m, p) => Math.max(m, p.tilesBack), 0);
  return maxBack + 1;
}

/**
 * Alto VISUAL de la casa en tiles (desde la base hacia el norte), incluyendo el TECHO: el
 * mayor `tilesBack + heightTiles` sobre TODAS las partes. Es mucho mayor que la huella de
 * colisión (el techo sobresale bastante) y es lo que hay que usar para separar casas en las
 * ciudades sin que un techo se monte sobre la casa de arriba. Ej. house1 = 2+10 = 12.
 */
export function houseVisualDepthTiles(def: HouseDef): number {
  return def.parts.reduce((m, p) => Math.max(m, p.tilesBack + p.heightTiles), 0);
}

/** Resultado de estampar una casa: tiles listos para volcar en las capas del mapa. */
export interface HouseStamp {
  /** Bandas de cuerpo → capa `ground_deco_over`. */
  deco: TileData[];
  /** Techo → capa `roof`. */
  roof: TileData[];
  /** Huella de colisión → capa `_collision` (toda la huella bloquea). */
  blocked: TileData[];
  /** Piso interior (huella de colisión) → capa `layer1_ground`. */
  floor: TileData[];
  /** Tiles "x,y" de la huella (para suprimir vegetación encima). */
  footprint: Set<string>;
  /**
   * Rectángulo del interior de la casa (en tiles) = el rectángulo de colisión ajustado al
   * cuerpo visible. Se usa para el área `interior` (efecto de oscurecer afuera + atenuar
   * techo al entrar). Ver docs/AREAS.md y LightingSystem.
   */
  interiorRect: MapRect;
}

/**
 * Estampa una casa anclada en la columna `anchorX`, con la base (fila de suelo de la
 * banda de adelante) en `baseY`. Devuelve los tiles para cada capa.
 *
 * Cada banda se ancla en la fila `baseY - tilesBack`. La huella de colisión cubre 8
 * columnas (`anchorX-4..anchorX+3`) × la profundidad (front→back) de la casa.
 */
export function stampHouse(anchorX: number, baseY: number, def: HouseDef): HouseStamp {
  const deco: TileData[] = [];
  const roof: TileData[] = [];

  for (const part of def.parts) {
    const row = baseY - part.tilesBack;
    if (part.layer === 'roof') {
      roof.push({ x: anchorX, y: row, sprite: part.key });
    } else {
      deco.push({ x: anchorX, y: row, sprite: part.key });
    }
  }

  // Colisión: la casa es TRANSITABLE (se entra adentro). Bloquea solo el CONTORNO de
  // paredes del rectángulo de colisión (ajustado al cuerpo visible, no a la base del
  // sprite, porque la casa está en perspectiva), menos la puerta; el interior queda
  // caminable. El `footprint` (Set) = el rectángulo, para suprimir vegetación y detectar
  // solapes entre casas.
  const depth = houseFootprintDepthTiles(def);
  const halfWidth = def.collision?.halfWidth ?? Math.floor(houseWidthTiles(def) / 2);
  const frontTb = def.collision?.front ?? 0;
  const backTb = def.collision?.back ?? depth - 1;
  const left = anchorX - halfWidth;
  const right = anchorX + halfWidth;
  const front = baseY - frontTb; // pared de adelante (Y mayor)
  const back = baseY - backTb; // pared de atrás (Y menor)
  const doorCols = new Set((def.doorCols ?? DEFAULT_DOOR_COLS).map((c) => anchorX + c));

  const blocked: TileData[] = [];
  const footprint = new Set<string>();
  // El `footprint` (rectángulo bounding) NO cambia: se usa para suprimir vegetación y detectar
  // solapes entre casas. Lo que cambia es `blocked`.
  for (let tx = left; tx <= right; tx++) {
    for (let ty = back; ty <= front; ty++) {
      footprint.add(`${tx},${ty}`);
    }
  }

  // Piso interior (`layer1_ground`): SOLO el interior de las paredes — `left+1..right-1` ×
  // `back+2..front+1` (corrido 1 tile hacia abajo/sur para calzar con el cuerpo visible).
  // Excluye la banda `_1` del frente (bajo ella queda pasto) y no desborda (sub-tiles 1×1 sin
  // bleed). Las paredes/techo se dibujan encima. Estos límites (`floor*`) definen TAMBIÉN el
  // rectángulo del área `interior` (el efecto de iluminar al entrar), así calzan EXACTO.
  const floorLeft = left + 1;
  const floorRight = right - 1;
  const floorTop = back + 2;
  const floorBottom = front + 1;
  const floor: TileData[] = [];
  for (let tx = floorLeft; tx <= floorRight; tx++) {
    for (let ty = floorTop; ty <= floorBottom; ty++) {
      floor.push({ x: tx, y: ty, sprite: houseFloorSprite(tx, ty) });
    }
  }

  // Colisión: si la casa tiene offsets explícitos en el sidecar (editados con la herramienta de
  // "Bloqueos de casas"), esos mandan. Si no, se cae al rectángulo paramétrico (perímetro − puerta).
  const explicit = houseBlockedOffsets(def.id);
  if (explicit) {
    for (const { dx, dy } of explicit) {
      blocked.push({ x: anchorX + dx, y: baseY + dy, sprite: 'blocked', blocked: true });
    }
  } else {
    for (let tx = left; tx <= right; tx++) {
      for (let ty = back; ty <= front; ty++) {
        const isPerimeter = tx === left || tx === right || ty === front || ty === back;
        const isDoor = ty === front && doorCols.has(tx);
        if (isPerimeter && !isDoor) {
          blocked.push({ x: tx, y: ty, sprite: 'blocked', blocked: true });
        }
      }
    }
  }

  // Interior (para el área `interior`): EXACTAMENTE el bounding box del PISO de madera. Así el
  // efecto de iluminar al entrar calza con el piso de la casa (mismo alto y largo), ni corto ni
  // desbordado.
  const interiorRect: MapRect = {
    x: floorLeft,
    y: floorTop,
    w: floorRight - floorLeft + 1,
    h: floorBottom - floorTop + 1,
  };

  return { deco, roof, blocked, floor, footprint, interiorRect };
}
